
import React, { useState, useEffect } from 'react';
import { firestore } from '../firebase';
import { uploadToCloudinary } from '../utils/cloudinary';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTrash, faEdit, faPlus, faGavel } from '@fortawesome/free-solid-svg-icons';
import './AdminPanel.css';

const AdminPolicies = () => {
    const [policies, setPolicies] = useState([]);
    const [selectedId, setSelectedId] = useState("");
    const [title, setTitle] = useState("");
    const [content, setContent] = useState("");
    const [file, setFile] = useState(null);
    const [manualUrl, setManualUrl] = useState("");
    const [existingFile, setExistingFile] = useState(null);

    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });

    useEffect(() => {
        fetchPolicies();
    }, []);

    const fetchPolicies = async () => {
        setLoading(true);
        try {
            const snapshot = await firestore.collection('policies').get();
            const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setPolicies(list);
        } catch (error) {
            console.error("Error fetching policies:", error);
            setStatus({ type: 'error', message: 'Failed to load policies list.' });
        }
        setLoading(false);
    };

    const handleSelect = (e) => {
        const id = e.target.value;
        setSelectedId(id);
        if (id === "new") {
            setTitle("");
            setContent("");
        } else {
            const policy = policies.find(p => p.id === id);
            if (policy) {
                setTitle(policy.title || policy.id);
                setContent(policy.content || "");
                setExistingFile(policy.file || null);
                setFile(null);
                setManualUrl("");
            }
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!title) {
            setStatus({ type: 'error', message: 'Title is required.' });
            return;
        }

        setLoading(true);
        try {
            let docRef;
            if (selectedId && selectedId !== 'new') {
                docRef = firestore.collection('policies').doc(selectedId);
            } else {
                docRef = firestore.collection('policies').doc();
            }

            let fileData = existingFile;
            if (file) {
                try {
                    const uploadedData = await uploadToCloudinary(file);
                    fileData = { name: file.name, url: uploadedData.url };
                } catch (uploadError) {
                    console.error("Cloudinary upload failed:", uploadError);
                    setStatus({ type: 'error', message: `Upload failed: ${uploadError.message}` });
                    setLoading(false);
                    return;
                }
            } else if (manualUrl) {
                fileData = { name: 'Linked Document', url: manualUrl };
            }

            await docRef.set({
                title: title,
                content: content,
                file: fileData || null,
                updatedAt: new Date(),
            }, { merge: true });

            setStatus({ type: 'success', message: 'Policy saved successfully!' });

            const savedData = { id: docRef.id, title, content, file: fileData || null, updatedAt: new Date() };

            setPolicies(prev => {
                if (selectedId === 'new' || !selectedId) {
                    return [...prev, savedData];
                } else {
                    return prev.map(p => p.id === selectedId ? { ...p, ...savedData } : p);
                }
            });

            if (selectedId === 'new') {
                setTitle("");
                setContent("");
                setFile(null);
                setManualUrl("");
                setExistingFile(null);
                setSelectedId("");
            }

        } catch (error) {
            console.error("Error saving policy:", error);
            setStatus({ type: 'error', message: 'Failed to save policy.' });
        }
        setLoading(false);
        setTimeout(() => setStatus({ type: '', message: '' }), 3000);
    };

    const handleDelete = async () => {
        if (!selectedId || selectedId === 'new') return;
        if (!window.confirm(`Delete policy "${title}"?`)) return;

        setLoading(true);
        try {
            await firestore.collection('policies').doc(selectedId).delete();

            setStatus({ type: 'success', message: 'Policy deleted.' });
            setTitle("");
            setContent("");

            setSelectedId("");
            await fetchPolicies();
        } catch (error) {
            console.error("Error deleting:", error);
            setStatus({ type: 'error', message: 'Failed to delete.' });
        }
        setLoading(false);
        setTimeout(() => setStatus({ type: '', message: '' }), 3000);
    };

    const handleCreateNew = () => {
        setSelectedId("new");
        setTitle("");
        setContent("");
        setFile(null);
        setManualUrl("");
        setExistingFile(null);
    };

    return (
        <div className="admin-section-container">
            <div className="section-header">
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <h2><FontAwesomeIcon icon={faGavel} className="header-icon" /> Manage Policies</h2>
                    <p>Create, edit, or remove company policies.</p>
                </div>
            </div>

            <div className="procedures-layout" style={{ marginTop: '30px' }}>
                {/* Admin Sidebar */}
                <div className="procedures-sidebar">
                    <button
                        onClick={handleCreateNew}
                        className="admin-btn pp-primary"
                        style={{ width: '100%', marginBottom: '20px', justifyContent: 'center' }}
                    >
                        <FontAwesomeIcon icon={faPlus} /> Create New Policy
                    </button>

                    <h3>All Policies</h3>
                    {policies.length === 0 ? <p style={{ textAlign: 'center', color: '#888' }}>No policies found.</p> :
                        policies.map((policy) => (
                            <div
                                key={policy.id}
                                className={`sidebar-item ${selectedId === policy.id ? 'active' : ''}`}
                                onClick={() => handleSelect({ target: { value: policy.id } })}
                            >
                                {policy.title || policy.id}
                            </div>
                        ))
                    }
                </div>

                <div className="procedure-details" style={{ minHeight: '600px' }}>
                    {selectedId ? (
                        <form onSubmit={handleSave} className="admin-form" style={{ marginTop: 0 }}>
                            <h3 style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '15px', marginBottom: '25px', color: '#f59e0b' }}>
                                {selectedId === 'new' ? 'Creating New Policy' : 'Editing Policy'}
                            </h3>

                            <div className="form-group">
                                <label>Policy Heading (Title)</label>
                                <input
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    className="pp-admin-input"
                                    placeholder="e.g., Code of Conduct"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Policy Content (News/Article Format)</label>
                                <textarea
                                    value={content}
                                    onChange={(e) => setContent(e.target.value)}
                                    placeholder="Enter details here..."
                                    className="pp-admin-textarea"
                                    rows="20"
                                    style={{ lineHeight: '1.6' }}
                                ></textarea>
                            </div>

                            <div className="form-group">
                                <label>Attached File (PDF, Image, etc.)</label>
                                {existingFile && (
                                    <div style={{ marginBottom: '10px', padding: '10px', background: '#f0f0f0', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <a href={existingFile.url} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>
                                            {existingFile.name}
                                        </a>
                                        <button
                                            type="button"
                                            className="admin-btn pp-danger"
                                            style={{ padding: '2px 8px', fontSize: '0.8rem', marginLeft: '10px' }}
                                            onClick={() => {
                                                if (window.confirm("Remove attached file?")) setExistingFile(null);
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faTrash} /> Remove
                                        </button>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    onChange={(e) => setFile(e.target.files[0])}
                                    className="pp-admin-input"
                                />

                                <div style={{ textAlign: 'center', margin: '15px 0', color: '#888', fontSize: '0.9rem' }}>- OR -</div>

                                <input
                                    type="text"
                                    placeholder="Paste document URL manually (e.g. Google Drive link)"
                                    className="pp-admin-input"
                                    value={manualUrl}
                                    onChange={(e) => setManualUrl(e.target.value)}
                                />
                            </div>

                            <div className="pp-form-actions">
                                <button type="submit" className="admin-btn pp-primary" disabled={loading}>
                                    <FontAwesomeIcon icon={faSave} /> {loading ? 'Saving...' : (selectedId === 'new' ? 'Create Policy' : 'Update Policy')}
                                </button>

                                {selectedId !== 'new' && (
                                    <button
                                        type="button"
                                        className="admin-btn pp-danger"
                                        onClick={handleDelete}
                                        disabled={loading}
                                        style={{ marginLeft: '10px' }}
                                    >
                                        <FontAwesomeIcon icon={faTrash} /> Delete Policy
                                    </button>
                                )}
                            </div>

                            {status.message && (
                                <div className={`pp-status-message ${status.type}`} style={{ marginTop: '20px' }}>
                                    {status.message}
                                </div>
                            )}
                        </form>
                    ) : (
                        <div className="no-selection" style={{ padding: '50px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <FontAwesomeIcon icon={faGavel} style={{ fontSize: '4rem', color: '#333', marginBottom: '20px' }} />
                            <p>Select a policy from the sidebar to edit,</p>
                            <p>or click "Create New Policy" to start fresh.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminPolicies;
